import {formatCurrency} from '@pinecast/common/helpers/format';
import {Crown} from '@pinecast/common/icons';
import Label from '@pinecast/common/Label';
import OptionButton, {
  OptionButtonGroup,
  RadioMark,
} from '@pinecast/common/OptionButton';
import ScreenReaderOnly from '@pinecast/common/ScreenReaderOnly';
import SelectCustom from '@pinecast/common/SelectCustom';
import type {
  DonationPage_podcast,
  DonationPage_tipJarSubscription,
} from '@pinecast/gql/__generated_tj__/DonationPage';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import type {ColorMap} from '@pinecast/styles';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

import PrivateEpisodeNote from './PrivateEpisodeNote';
import {
  hasGatedEpisodes,
  layOutAmounts,
  unlocksPrivateEpisodes,
} from './tipAmounts';

const messages = {
  headingSubscribe: defineMessage({
    description: 'Heading above the contribution amounts on the tip jar page',
    defaultMessage: 'Choose your contribution amount',
  }),
  headingUpdate: defineMessage({
    description:
      'Heading above the contribution amounts on the tip jar page, when the listener already has a recurring subscription to change',
    defaultMessage: 'Change your monthly contribution',
  }),
};

// The card face, shared by the three amount cards and the dropdown card beside
// them so the two read as one row of choices. Mirrors what OptionButton paints,
// because the dropdown card cannot be one: a select inside a button is a second
// tab stop that swallows the button's own clicks. It draws no focus ring of its
// own for the same reason: focus is on the select, which draws its own.
const cardFace = (designColors: ColorMap, selected: boolean) => ({
  backgroundColor: designColors.contentBackground,
  border: `1px solid ${
    selected ? designColors.borderBlueNormal : designColors.contentBackground
  }`,
  borderRadius: '4px',
  boxShadow: selected ? `0 0 0 4px ${designColors.accentBlueLight}` : undefined,
  transition: 'border 0.2s, box-shadow 0.2s',
});

const MARK_SIZE = 22;

/**
 * The mark beside an amount in the dropdown: a gold crown for the amounts that
 * reach the show's private episodes, and a struck grey one for those that do
 * not. Struck rather than merely grey, so the two are told apart by shape and
 * not only by colour.
 *
 * The strike is drawn twice — once thick in the tile's own fill, then thin in
 * the ink over it. The fill pass knocks a gap out of the crown on either side
 * of the line, so the strike reads as passing over the crown rather than
 * merging into it. Both stop short of the tile's corners, which keeps the line
 * reading as a mark on the tile rather than as a cut through it.
 */
const AmountMark = ({unlocks}: {unlocks: boolean}) => {
  const css = useCSS();
  const designColors = useColors();

  const ink = unlocks ? designColors.promotionText : designColors.badgeGrayText;
  const fill = unlocks
    ? designColors.promotionBackground
    : designColors.badgeGrayBackground;
  const edge = unlocks
    ? designColors.badgeYellowText
    : designColors.borderGrayNormal;

  // Inset from the tile's edge rather than run past it, so the diagonal ends a
  // few pixels short of each corner.
  const strike = {
    left: '2px',
    position: 'absolute' as const,
    right: '2px',
    top: '50%',
    transform: 'rotate(-45deg)',
  };

  return (
    <span
      aria-hidden="true"
      className={css({
        alignItems: 'center',
        backgroundColor: fill,
        border: `1px solid ${edge}`,
        borderRadius: '5px',
        boxSizing: 'border-box',
        color: ink,
        display: 'inline-flex',
        flex: '0 0 auto',
        height: `${MARK_SIZE}px`,
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
        width: `${MARK_SIZE}px`,
      })}
    >
      <Crown size={13} />
      {!unlocks && (
        <>
          {/* 1.5px line plus one line-width of gap either side. */}
          <span
            className={css({
              ...strike,
              backgroundColor: fill,
              height: '4.5px',
              marginTop: '-2.25px',
            })}
          />
          <span
            className={css({
              ...strike,
              backgroundColor: ink,
              height: '1.5px',
              marginTop: '-0.75px',
            })}
          />
        </>
      )}
    </span>
  );
};

const AmountCard = ({
  amount,
  isCurrent,
  isThreshold,
  onClick,
  podcast,
  selected,
}: {
  amount: number;
  isCurrent: boolean;
  isThreshold: boolean;
  onClick: () => void;
  podcast: DonationPage_podcast;
  selected: boolean;
}) => {
  const css = useCSS();
  const designColors = useColors();

  return (
    <div className={css({display: 'flex', flexDirection: 'column'})}>
      {/* Reserved on every card so the amounts share a baseline whether or not
          the card is the one carrying the band. */}
      <div
        className={css({
          alignItems: 'flex-end',
          display: 'flex',
          flex: '0 0 auto',
          height: '34px',

          // Stacked, there is no neighbouring card to line up with, so the
          // reserved strip is just a gap above every card.
          '@media (max-width: 600px)': {
            height: isThreshold ? '34px' : 0,
          },
        })}
      >
        {isThreshold && (
          <div
            className={css({
              alignItems: 'center',
              backgroundColor: designColors.promotionBackground,
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              boxSizing: 'border-box',
              color: designColors.promotionText,
              display: 'flex',
              fontSize: '12px',
              gap: '7px',
              letterSpacing: '0.06em',
              lineHeight: '16px',
              padding: '9px 12px',
              textTransform: 'uppercase',
              width: '100%',
            })}
          >
            <Crown size={14} />
            <FormattedMessage
              description="Band across the top of the tip jar amount that first unlocks a show's private episodes"
              defaultMessage="Private episodes"
            />
          </div>
        )}
      </div>
      <OptionButton
        onClick={onClick}
        selected={selected}
        style={{
          flex: '1 1 auto',
          // A card is a big target with no edge of its own until it is picked,
          // which leaves nothing to say it can be picked. Borrowing the border
          // from the selected state — without its ring — marks the card under
          // the pointer as the one a click would take.
          //
          // styled() merges `style` one key deep, so naming this selector
          // replaces OptionButton's own rule for it rather than adding to it,
          // and its text shadow has to be restated to survive.
          ':hover:not(:focus):not(:active)': {
            border: `1px solid ${designColors.borderBlueNormal}`,
            textShadow: selected ? undefined : '0 2px 2px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <span
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            height: '100%',
            padding: '10px 4px 4px',
            width: '100%',
          })}
        >
          <span className={css({display: 'flex', flexDirection: 'column'})}>
            <RadioMark
              $alignInput="center"
              $checked={selected}
              $disabled={false}
            >
              <span
                className={css({
                  display: 'block',
                  fontFamily:
                    '"Ginto Nord Condensed", "Monument Grotesk Bold", sans-serif',
                  fontSize: '40px',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  lineHeight: '44px',
                })}
              >
                {formatCurrency(amount, podcast.tipJarCurrency)}
              </span>
            </RadioMark>
            <span
              className={css({
                color: designColors.contentTextMuted,
                fontSize: '14px',
                lineHeight: '20px',
                paddingLeft: '24px',
              })}
            >
              <FormattedMessage
                description="Unit under a contribution amount on the tip jar page"
                defaultMessage="per month"
              />
            </span>
            {isCurrent && (
              <span
                className={css({
                  fontSize: '13px',
                  lineHeight: '18px',
                  paddingLeft: '24px',
                })}
              >
                <FormattedMessage
                  description="Label for the existing subscription's tip amount"
                  defaultMessage="Your current subscription"
                />
              </span>
            )}
          </span>
          <PrivateEpisodeNote amount={amount} podcast={podcast} />
        </span>
      </OptionButton>
    </div>
  );
};

const TipAmountSelection = ({
  onChange,
  podcast,
  subscription,
  value,
}: {
  onChange: (value: number) => void;
  podcast: DonationPage_podcast;
  subscription: DonationPage_tipJarSubscription | null;
  value: number | null;
}) => {
  const css = useCSS();
  const designColors = useColors();
  const headingId = React.useId();
  const optionRow = css({
    alignItems: 'center',
    display: 'inline-flex',
    gap: '8px',
    minHeight: `${MARK_SIZE}px`,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  });

  const {tiers, others, threshold} = layOutAmounts(podcast);
  const gatesEpisodes = hasGatedEpisodes(podcast);
  const otherSelected = value !== null && others.includes(value);

  const isCurrentAmount = (amount: number) =>
    amount === subscription?.amount &&
    podcast.tipJarCurrency === subscription.currency;

  return (
    <div
      className={css({display: 'flex', flexDirection: 'column', gap: '14px'})}
    >
      <h2
        id={headingId}
        className={css({
          fontFamily: 'Monument Grotesk Bold',
          fontSize: '22px',
          fontWeight: 400,
          lineHeight: '28px',
          margin: 0,
        })}
      >
        <FormattedMessage
          {...(subscription
            ? messages.headingUpdate
            : messages.headingSubscribe)}
        />
      </h2>

      {/* Each card says for itself whether it includes private episodes, so the
          group needs no more of a name than the heading above it. */}
      <OptionButtonGroup
        aria-labelledby={headingId}
        direction="horizontal"
        spacing={16}
        wrapperStyle={{
          alignItems: 'stretch',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',

          '@media (max-width: 600px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
          },
        }}
      >
        {tiers.map(amount => (
          <AmountCard
            amount={amount}
            isCurrent={isCurrentAmount(amount)}
            isThreshold={amount === threshold}
            key={amount}
            onClick={() => {
              onChange(amount);
            }}
            podcast={podcast}
            selected={value === amount}
          />
        ))}
      </OptionButtonGroup>

      {others.length > 0 && (
        <div
          className={css({
            ...cardFace(designColors, otherSelected),
            alignItems: 'flex-end',
            boxSizing: 'border-box',
            columnGap: '10px',
            display: 'flex',
            flexWrap: 'wrap',
            padding: '14px 16px',
            rowGap: '10px',
          })}
        >
          {/* A native label, so the select keeps its name once it shows an
              amount and not the placeholder. The note stays outside it: a
              label's text is the name of the control inside. */}
          <Label
            labelStyle={{fontSize: 16, lineHeight: '24px'}}
            style={{flex: '0 0 auto', marginBottom: 0}}
            text={
              <FormattedMessage
                description="Label for the dropdown of contribution amounts beyond the three suggested on the tip jar page"
                defaultMessage="Another amount"
              />
            }
          >
            <SelectCustom
              onChange={newValue => {
                if (newValue !== '') {
                  onChange(Number(newValue));
                }
              }}
              options={[
                {
                  key: '',
                  render: () => (
                    <span className={optionRow}>
                      <FormattedMessage
                        description="Placeholder in the dropdown of other contribution amounts on the tip jar page"
                        defaultMessage="Choose another amount"
                      />
                    </span>
                  ),
                },
                ...others.map(amount => ({
                  key: amount,
                  render: () => (
                    <span className={optionRow}>
                      {gatesEpisodes && (
                        <AmountMark
                          unlocks={unlocksPrivateEpisodes(podcast, amount)}
                        />
                      )}
                      <FormattedMessage
                        description="An amount in the dropdown of other contribution amounts on the tip jar page. {amount} is a formatted currency amount."
                        defaultMessage="{amount} per month"
                        values={{
                          amount: formatCurrency(
                            amount,
                            podcast.tipJarCurrency,
                          ),
                        }}
                      />
                      {/* The tile above is a picture; this is what it says. */}
                      {gatesEpisodes && (
                        <ScreenReaderOnly>
                          {unlocksPrivateEpisodes(podcast, amount) ? (
                            <FormattedMessage
                              description="Spoken after an amount in the dropdown of other contribution amounts, where the amount is enough for the show's private episodes. Shown as a gold crown."
                              defaultMessage="Private episodes included"
                            />
                          ) : (
                            <FormattedMessage
                              description="Spoken after an amount in the dropdown of other contribution amounts, where the amount is below the show's minimum for private episodes. Shown as a struck-out crown."
                              defaultMessage="No private episodes"
                            />
                          )}
                        </ScreenReaderOnly>
                      )}
                    </span>
                  ),
                })),
              ]}
              style={{flex: '0 0 auto'}}
              value={otherSelected ? value : ''}
            />
          </Label>
          {/* Pushes the note to the end of the line on a wide viewport, and
              onto a line of its own — left aligned — on a narrow one. */}
          <span
            aria-hidden="true"
            className={css({flex: '1 1 auto', minWidth: '16px'})}
          />
          <div
            className={css({
              alignItems: 'center',
              display: 'flex',
              flex: '0 0 auto',
              // The height of the select, so a one-line note lines up with it.
              minHeight: '32px',
            })}
          >
            <PrivateEpisodeNote
              amount={otherSelected ? value : null}
              podcast={podcast}
              standalone
            />
          </div>
          {otherSelected && isCurrentAmount(value) && (
            <span
              className={css({
                flexBasis: '100%',
                fontSize: '13px',
                lineHeight: '18px',
              })}
            >
              <FormattedMessage
                description="Label for the existing subscription's tip amount"
                defaultMessage="Your current subscription"
              />
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TipAmountSelection;
